import {
    Component,
    Input,
    ElementRef,
    ViewChildren,
    QueryList,
    AfterViewInit,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
  } from '@angular/core';
import { CommonModule } from '@angular/common';
  
  @Component({
    selector: 'slot-counter',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './slot-counter.component.html',
    styleUrls: ['./slot-counter.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { role: 'group', '[attr.aria-label]': 'ariaLabel' },
  })
  export class SlotCounterComponent implements AfterViewInit, OnChanges {
    private _number = 0;
  
    /** Public API: <slot-counter [number]="..."> */
    @Input()
    set number(v: number) {
      this._number = this.normalize(v);
      this.buildModel();
      this._dirty = true;
    }
    get number(): number {
      return this._number;
    }
  
    // Animation options (can easily become @Input)
    loops = 3;
    baseDuration = 1200;
    cascadeDelay = 120;
    groupThousands = true;
    separatorChar = '\u2009'; // thin space
  
    // Template model
    groups: number[][] = [];
    ariaLabel = 'Counter 0';
  
    @ViewChildren('reel') reels!: QueryList<ElementRef<HTMLDivElement>>;
  
    private _ready = false;
    private _dirty = false;
  
    constructor(private host: ElementRef<HTMLElement>) {}
  
    ngAfterViewInit(): void {
      this._ready = true;
      this.runIfDirty();
    }
  
    ngOnChanges(changes: SimpleChanges): void {
      if ('number' in changes) {
        this.ariaLabel = 'Counter ' + this._number;
        this._dirty = true;
        this.runIfDirty();
      }
    }
  
    /** ---------------- Animation ---------------- */
  
    private runIfDirty(): void {
      if (!this._ready || !this._dirty) return;
      setTimeout(() => this.runAnimation(), 0);
      this._dirty = false;
    }
  
    private runAnimation(): void {
      if (!this.reels || this.reels.length === 0) return;
  
      const reduced = this.prefersReducedMotion();
      let indexFromRight = 0;
      const all = this.reels.toArray();
  
      for (let i = all.length - 1; i >= 0; i--) {
        const reel = all[i].nativeElement;
        const first = reel.firstElementChild as HTMLElement | null;
        if (!first) continue;
  
        const lineHeight = first.getBoundingClientRect().height;
        const totalSteps = this.computeSteps(reel);
        const duration = this.baseDuration + indexFromRight * this.cascadeDelay;
  
        reel.style.transitionProperty = 'transform';
        reel.style.transitionTimingFunction = 'cubic-bezier(.17,.84,.44,1)';
        reel.style.transitionDuration = `${duration}ms`;
  
        if (!reduced)
          reel.style.transform = `translate3d(0, ${-totalSteps * lineHeight}px, 0)`;
        else {
          reel.style.transitionDuration = '0ms';
          reel.style.transform = `translate3d(0, ${-totalSteps * lineHeight}px, 0)`;
        }
  
        indexFromRight++;
      }
    }
  
    private computeSteps(reelEl: HTMLDivElement): number {
      const spans = reelEl.querySelectorAll('span');
      if (spans.length === 0) return 0;
      const last = spans[spans.length - 1].textContent || '0';
      const finalDigit = this.toDigit(last);
      return this.loops * 10 + finalDigit;
    }
  
    /** ---------------- Data model ---------------- */
  
    private buildModel(): void {
      const digits = this.splitDigits(this._number);
      this.groups = this.groupThousands ? this.groupByThousands(digits) : [digits];
    }
  
    private splitDigits(n: number): number[] {
      const s = Math.floor(Math.abs(n)).toString();
      return [...s].map((ch) => this.toDigit(ch));
    }
  
    private groupByThousands(digits: number[]): number[][] {
      const groups: number[][] = [];
      let tmp: number[] = [];
      for (let i = 0; i < digits.length; i++) {
        tmp.push(digits[i]);
        const left = digits.length - i - 1;
        if (left % 3 === 0 && left !== 0) {
          groups.push(tmp);
          tmp = [];
        }
      }
      if (tmp.length) groups.push(tmp);
      return groups;
    }
  
    /** ---------------- Helpers ---------------- */
  
    private normalize(v: any): number {
      const n = Number(v);
      return isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    }
  
    private toDigit(ch: string | number): number {
      const n = typeof ch === 'number' ? ch : ch.charCodeAt(0) - 48;
      return n >= 0 && n <= 9 ? n : 0;
    }
  
    private prefersReducedMotion(): boolean {
      if (typeof window === 'undefined' || !('matchMedia' in window)) return false;
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }
  